import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Check, Plus } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { API, PACKAGES, ADDON, fmtMoney } from "@/lib/data";

const inputCls =
  "w-full rounded-xl border border-emerald-500/25 bg-[#06130D]/70 px-4 py-3 text-sm text-amber-50 placeholder-emerald-100/30 outline-none transition-colors focus:border-amber-400/70";

export const Booking = ({ selected, onSelect, packages = PACKAGES, addon = ADDON }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [addonOn, setAddonOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/availability`).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, []);

  const seasonStart = avail ? new Date(`${avail.start}T00:00:00`) : new Date();
  const seasonEnd = avail ? new Date(`${avail.end}T00:00:00`) : new Date();
  const disabledDays = [{ before: seasonStart }, { after: seasonEnd }];

  const pkg = packages.find((p) => p.id === selected) || packages[1];
  const total = pkg.price + (addonOn ? addon.price : 0);
  const deposit = total / 2;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!date) {
      toast.error("Pick an installation date on the calendar first.");
      return;
    }
    setLoading(true);
    try {
      const { data: b } = await axios.post(`${API}/bookings`, {
        ...form,
        install_date: date.toISOString().slice(0, 10),
        package_id: pkg.id,
        addon: addonOn,
      });
      const { data: c } = await axios.post(`${API}/payments/checkout`, {
        booking_id: b.booking.id,
        origin_url: window.location.origin,
      });
      toast.success("Redirecting to secure checkout…");
      window.location.href = c.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us instead.");
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="relative border-t border-amber-500/10 bg-[#08170F] py-24 sm:py-32" data-testid="booking-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="mb-4 font-mono-x text-xs uppercase tracking-[0.28em] text-amber-400">Reserve Your Install Date</p>
          <h2 className="font-display max-w-2xl text-3xl font-bold tracking-tight text-amber-50 sm:text-5xl">
            Book your spot with a <span className="italic text-amber-400">50% deposit.</span>
          </h2>
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-5">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="space-y-6 rounded-3xl border border-amber-500/20 bg-[#0B1D14]/90 p-6 sm:p-8 lg:col-span-3"
          >
            <div>
              <p className="mb-3 font-mono-x text-[11px] uppercase tracking-[0.22em] text-emerald-100/60">1 · Choose your package</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {packages.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => onSelect(p.id)}
                    data-testid={`booking-package-${p.id}`}
                    className={`rounded-xl border px-4 py-3 text-left transition-all ${
                      p.id === pkg.id
                        ? "border-amber-400 bg-amber-500/10 gold-glow-soft"
                        : "border-emerald-500/25 hover:border-amber-500/40"
                    }`}
                  >
                    <span className="font-display block text-lg font-bold text-amber-50">{fmtMoney(p.price)}</span>
                    <span className="block text-[11px] leading-snug text-emerald-100/60">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-3 font-mono-x text-[11px] uppercase tracking-[0.22em] text-emerald-100/60">2 · Pick your installation date</p>
              <div className="rounded-xl border border-emerald-500/25 bg-[#06130D]/70 p-3" data-testid="booking-form-date">
                <DayPicker
                  key={avail ? avail.start : "loading"}
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  defaultMonth={seasonStart}
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-emerald-500/15 pt-3 text-xs text-emerald-100/60">
                  <span>
                    {avail
                      ? `Booking ${seasonStart.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ${seasonEnd.toLocaleDateString("en-US", { month: "long", day: "numeric" })} · pick any day that works`
                      : "Loading calendar…"}
                  </span>
                  <span className="font-mono-x uppercase tracking-widest text-amber-300" data-testid="booking-selected-date">
                    {date ? date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "No date picked"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p className="mb-3 font-mono-x text-[11px] uppercase tracking-[0.22em] text-emerald-100/60">3 · Your details</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="booking-form-name" className={inputCls} />
                <input required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="booking-form-phone" className={inputCls} />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="booking-form-email" className={inputCls} />
                <input required placeholder="Home address" value={form.address} onChange={set("address")} data-testid="booking-form-address" className={inputCls} />
                <textarea
                  placeholder="Notes for our crew (gate codes, tricky rooflines, pets…)"
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  className={`${inputCls} sm:col-span-2 resize-none`}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAddonOn((a) => !a)}
              data-testid="booking-addon-checkbox"
              className={`flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all ${
                addonOn ? "border-amber-400 bg-amber-500/10 gold-glow-soft" : "border-emerald-500/25 hover:border-amber-500/40"
              }`}
            >
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${addonOn ? "border-amber-400 bg-amber-400" : "border-emerald-500/40"}`}>
                {addonOn ? <Check size={14} className="text-stone-950" /> : <Plus size={13} className="text-emerald-100/50" />}
              </span>
              <span>
                <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-amber-50">
                  {addon.name}
                  <span className="rounded-full bg-amber-500/20 px-2.5 py-0.5 font-mono-x text-[10px] uppercase tracking-widest text-amber-300">+{fmtMoney(addon.price)}/yr</span>
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-emerald-100/60">{addon.description}</span>
              </span>
            </button>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit-deposit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 transition-all hover:from-amber-400 hover:to-amber-300 gold-glow disabled:opacity-60"
            >
              <Lock size={15} />
              {loading ? "Preparing secure checkout…" : `Pay ${fmtMoney(deposit)} Deposit & Book`}
            </button>
            <p className="text-center font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/40">
              Secure card payment via Stripe · Remaining {fmtMoney(total - deposit)} due after installation
            </p>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-2"
          >
            <div className="sticky top-24 overflow-hidden rounded-3xl border border-amber-500/20 bg-[#0B1D14]/90" data-testid="booking-summary">
              <div className="relative h-36">
                <img src={pkg.image} alt={pkg.name} className="h-full w-full object-cover" style={{ objectPosition: "center 72%" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D14] to-transparent" />
                <span className="absolute bottom-3 left-5 font-mono-x text-[10px] uppercase tracking-[0.22em] text-amber-300">{pkg.badge}</span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold text-amber-50">{pkg.name}</h3>
                <p className="mt-1 font-mono-x text-[11px] uppercase tracking-widest text-amber-300/80">
                  {date ? `Install: ${date.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" })}` : "Pick an install date on the calendar"}
                </p>
                <div className="mt-5 space-y-2.5 text-sm">
                  <div className="flex justify-between text-emerald-100/70">
                    <span>Package</span><span>{fmtMoney(pkg.price)}</span>
                  </div>
                  {addonOn && (
                    <div className="flex justify-between text-emerald-100/70">
                      <span>Storage & maintenance</span><span>{fmtMoney(addon.price)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-amber-500/15 pt-2.5 font-semibold text-amber-50">
                    <span>Season total</span><span>{fmtMoney(total)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-100/70">
                    <span>Due after installation</span><span>{fmtMoney(total - deposit)}</span>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl bg-gradient-to-r from-amber-500/15 to-amber-400/5 p-4 text-center">
                  <p className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-amber-300">Deposit due today</p>
                  <p className="font-display mt-1 text-4xl font-black text-amber-400 text-glow-gold" data-testid="booking-deposit-amount">{fmtMoney(deposit)}</p>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
